import { useMemo, useState } from "react";
import StatusBadge from "@/components/base/StatusBadge";
import Pagination from "@/components/base/Pagination";
import { Skeleton } from "@/components/base/Loading";
import { usePagination } from "@/hooks/usePagination";
import { formatFullDate } from "@/lib/format";
import { APPLICATION_STATUS_META, editionLabel } from "@/lib/license";
import { downloadCsv } from "@/lib/csv";
import BatchConfirmModal from "@/pages/admin/components/BatchConfirmModal";
import type { AdminApplication, BatchResult } from "@/pages/admin/hooks/useAdminData";

type Filter = "pending" | "approved" | "rejected" | "renewal" | "all";
type EmailInfo = { sent: boolean; reason?: string };

interface ApplicationReviewProps {
  applications: AdminApplication[];
  loading: boolean;
  onApprove: (id: number, note: string, months: number) => Promise<{ email?: EmailInfo }>;
  onReject: (id: number, note: string) => Promise<{ email?: EmailInfo }>;
  onApproveMany: (ids: number[], note: string, months: number) => Promise<BatchResult>;
  onRejectMany: (ids: number[], note: string) => Promise<BatchResult>;
  notify: (text: string, tone?: "success" | "error") => void;
}

const FILTERS: { key: Filter; label: string }[] = [
  { key: "pending", label: "待审批" },
  { key: "approved", label: "已通过" },
  { key: "rejected", label: "已驳回" },
  { key: "renewal", label: "续期申请" },
  { key: "all", label: "全部" },
];

const PAGE_SIZE = 8;

const DURATIONS = [
  { value: 12, label: "12 个月" },
  { value: 24, label: "24 个月" },
  { value: 36, label: "36 个月" },
];

function emailMessage(base: string, email?: EmailInfo): string {
  if (email?.sent) return `${base}，通知邮件已发送`;
  if (email?.reason === "email_not_configured") return `${base}（邮件通知尚未配置，暂未发送）`;
  return `${base}，但通知邮件发送失败`;
}

function batchMessage(result: BatchResult, action: "approve" | "reject"): string {
  const verb = action === "approve" ? "通过并处理" : "驳回";
  const parts = [`已批量${verb} ${result.processed} 条申请`];
  if (result.skipped > 0) parts.push(`跳过 ${result.skipped} 条`);
  if (result.emailSent > 0) parts.push(`邮件已发 ${result.emailSent} 封`);
  if (result.emailFailed > 0) parts.push(`邮件未发 ${result.emailFailed} 封`);
  return parts.join("，");
}

export default function ApplicationReview({
  applications,
  loading,
  onApprove,
  onReject,
  onApproveMany,
  onRejectMany,
  notify,
}: ApplicationReviewProps) {
  const [filter, setFilter] = useState<Filter>("pending");
  const [panel, setPanel] = useState<{ id: number; type: "approve" | "reject" } | null>(null);
  const [note, setNote] = useState("");
  const [months, setMonths] = useState(12);
  const [busy, setBusy] = useState(false);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [batch, setBatch] = useState<"approve" | "reject" | null>(null);
  const [batchNote, setBatchNote] = useState("");
  const [batchMonths, setBatchMonths] = useState(12);
  const [batchBusy, setBatchBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    const term = keyword.trim().toLowerCase();
    return applications.filter((item) => {
      if (filter === "renewal") {
        if (item.renew_license_id == null) return false;
      } else if (filter !== "all" && item.status !== filter) {
        return false;
      }
      if (!term) return true;
      return (
        item.applicant_name.toLowerCase().includes(term) ||
        item.email.toLowerCase().includes(term) ||
        (item.company ?? "").toLowerCase().includes(term) ||
        item.product_name.toLowerCase().includes(term) ||
        (item.machine_code ?? "").toLowerCase().includes(term)
      );
    });
  }, [applications, filter, keyword]);

  const { page, totalPages, total, pageSize, pagedItems, setPage } = usePagination(
    filtered,
    PAGE_SIZE,
    `${filter}|${keyword}`,
  );

  const selectable = useMemo(() => pagedItems.filter((item) => item.status === "pending"), [pagedItems]);
  const allSelectableChecked = selectable.length > 0 && selectable.every((item) => selected.has(item.id));
  const selectedApplications = useMemo(
    () => applications.filter((item) => selected.has(item.id)),
    [applications, selected],
  );

  const resetSelection = () => {
    setSelected(new Set());
    setBatch(null);
    setBatchNote("");
    setConfirmOpen(false);
  };

  const changeFilter = (next: Filter) => {
    setFilter(next);
    setKeyword("");
    resetSelection();
    closePanel();
  };

  const handleExport = () => {
    if (filtered.length === 0) {
      notify("当前筛选下没有可导出的申请", "error");
      return;
    }
    downloadCsv(
      `授权申请台账_${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { key: "id", label: "申请编号", value: (row) => row.id },
        { key: "product", label: "产品", value: (row) => row.product_name },
        { key: "version", label: "版本", value: (row) => row.product_version || "—" },
        { key: "edition", label: "版本类型", value: (row) => editionLabel(row.edition) },
        { key: "seats", label: "席位", value: (row) => row.seat_count },
        { key: "applicant", label: "申请人", value: (row) => row.applicant_name },
        { key: "email", label: "邮箱", value: (row) => row.email },
        { key: "company", label: "公司", value: (row) => row.company || "—" },
        { key: "machine", label: "机器码", value: (row) => row.machine_code || "—" },
        { key: "type", label: "申请类型", value: (row) => (row.renew_license_id != null ? "续期申请" : "首次申请") },
        { key: "status", label: "状态", value: (row) => APPLICATION_STATUS_META[row.status]?.label ?? row.status },
        { key: "note", label: "审批备注", value: (row) => row.reviewer_note || "—" },
        { key: "created", label: "提交时间", value: (row) => formatFullDate(row.created_at) },
        { key: "reviewed", label: "审批时间", value: (row) => (row.reviewed_at ? formatFullDate(row.reviewed_at) : "—") },
      ],
      filtered,
    );
    notify(`已导出 ${filtered.length} 条申请台账`, "success");
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelectableChecked) {
        selectable.forEach((item) => next.delete(item.id));
      } else {
        selectable.forEach((item) => next.add(item.id));
      }
      return next;
    });
  };

  const openPanel = (id: number, type: "approve" | "reject") => {
    resetSelection();
    setPanel({ id, type });
    setNote("");
    setMonths(12);
  };

  const closePanel = () => {
    setPanel(null);
    setNote("");
    setBusy(false);
  };

  const submitApprove = async (application: AdminApplication) => {
    setBusy(true);
    try {
      const result = await onApprove(application.id, note.trim(), months);
      const base =
        application.renew_license_id != null
          ? `已通过「${application.product_name}」的续期申请，许可证有效期已顺延`
          : `已通过并签发「${application.product_name}」许可证`;
      notify(emailMessage(base, result.email), "success");
      closePanel();
    } catch (err) {
      notify(err instanceof Error ? err.message : "审批失败", "error");
      setBusy(false);
    }
  };

  const submitReject = async (application: AdminApplication) => {
    setBusy(true);
    try {
      const result = await onReject(application.id, note.trim());
      notify(emailMessage(`已驳回「${application.product_name}」申请`, result.email), "success");
      closePanel();
    } catch (err) {
      notify(err instanceof Error ? err.message : "操作失败", "error");
      setBusy(false);
    }
  };

  const submitBatch = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBatchBusy(true);
    try {
      if (batch === "approve") {
        const result = await onApproveMany(ids, batchNote.trim(), batchMonths);
        notify(batchMessage(result, "approve"), "success");
      } else if (batch === "reject") {
        const result = await onRejectMany(ids, batchNote.trim());
        notify(batchMessage(result, "reject"), "success");
      }
      resetSelection();
    } catch (err) {
      notify(err instanceof Error ? err.message : "批量操作失败", "error");
    } finally {
      setBatchBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        {[0, 1, 2].map((item) => (
          <div key={item} className="rounded-lg border border-background-200 bg-background-50 p-5">
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="mt-4 h-4 w-2/3" />
            <Skeleton className="mt-4 h-9 w-40" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative w-full lg:max-w-sm">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line" />
          </span>
          <input
            type="text"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索申请人、邮箱、公司、产品或机器码"
            className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-9 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
          {keyword && (
            <button
              type="button"
              onClick={() => setKeyword("")}
              className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
              aria-label="清除搜索"
            >
              <i className="ri-close-line" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((item) => {
            const count = applications.filter((app) =>
              item.key === "renewal"
                ? app.renew_license_id != null
                : item.key === "all"
                  ? true
                  : app.status === item.key,
            ).length;
            const active = item.key === filter;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => changeFilter(item.key)}
                className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                }`}
              >
                {item.label}
                <span className={active ? "text-background-200" : "text-foreground-400"}>{count}</span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={filtered.length === 0}
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="flex h-4 w-4 items-center justify-center text-primary-700">
            <i className="ri-download-2-line" />
          </span>
          导出申请台账
        </button>
      </div>

      {selectable.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-foreground-800">
            <input
              type="checkbox"
              checked={allSelectableChecked}
              onChange={toggleSelectAll}
              className="h-4 w-4 cursor-pointer accent-primary-600"
            />
            全选本页待审批（{selectable.length}）
            {selected.size > 0 && (
              <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                已选 {selected.size}
              </span>
            )}
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={selected.size === 0}
              onClick={() => setBatch(batch === "approve" ? null : "approve")}
              className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                batch === "approve"
                  ? "bg-primary-600 text-background-50"
                  : "border border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100"
              }`}
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-check-double-line" />
              </span>
              批量通过
            </button>
            <button
              type="button"
              disabled={selected.size === 0}
              onClick={() => setBatch(batch === "reject" ? null : "reject")}
              className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                batch === "reject"
                  ? "bg-accent-600 text-background-50"
                  : "border border-accent-300 bg-accent-50 text-accent-800 hover:bg-accent-100"
              }`}
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-close-circle-line" />
              </span>
              批量驳回
            </button>
          </div>
        </div>
      )}

      {batch && selected.size > 0 && (
        <div
          className={`mb-4 rounded-md border p-4 ${
            batch === "approve" ? "border-primary-200 bg-primary-50" : "border-accent-200 bg-accent-50"
          }`}
        >
          <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
            <span
              className={`flex h-4 w-4 items-center justify-center ${
                batch === "approve" ? "text-primary-700" : "text-accent-700"
              }`}
            >
              <i className={batch === "approve" ? "ri-check-double-line" : "ri-close-circle-line"} />
            </span>
            {batch === "approve"
              ? `批量通过并签发 ${selected.size} 条申请`
              : `批量驳回 ${selected.size} 条申请`}
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {batch === "approve" && (
              <label className="flex flex-col gap-1.5 text-xs text-foreground-600">
                授权有效期（应用于全部）
                <select
                  value={batchMonths}
                  onChange={(event) => setBatchMonths(Number(event.target.value))}
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                >
                  {DURATIONS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="flex flex-col gap-1.5 text-xs text-foreground-600">
              {batch === "approve" ? "审批备注（选填）" : "驳回原因（将展示给申请人）"}
              <input
                type="text"
                value={batchNote}
                onChange={(event) => setBatchNote(event.target.value)}
                placeholder={batch === "approve" ? "例如：批量核验通过" : "例如：信息不完整，请重新提交"}
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
              />
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={batchBusy || selected.size === 0}
              onClick={() => setConfirmOpen(true)}
              className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-background-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                batch === "approve" ? "bg-primary-500 hover:bg-primary-600" : "bg-accent-600 hover:bg-accent-700"
              }`}
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-check-double-line" />
              </span>
              核对并提交（{selected.size}）
            </button>
            <button
              type="button"
              onClick={() => {
                setBatch(null);
                setBatchNote("");
              }}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
            >
              取消
            </button>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-inbox-line text-2xl" />
          </span>
          <p className="text-sm font-medium text-foreground-800">该状态下暂无申请</p>
          <p className="max-w-sm text-xs text-foreground-500">切换上方筛选条件查看其它状态的授权申请。</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {pagedItems.map((application) => {
            const meta =
              APPLICATION_STATUS_META[application.status] ?? {
                label: application.status,
                className: "bg-background-200 text-foreground-700",
              };
            const isPending = application.status === "pending";
            const isRenewal = application.renew_license_id != null;
            const openApprove = panel?.id === application.id && panel.type === "approve";
            const openReject = panel?.id === application.id && panel.type === "reject";
            const checked = selected.has(application.id);

            return (
              <article
                key={application.id}
                className={`rounded-lg border bg-background-50 p-5 transition-colors ${
                  checked ? "border-primary-400" : "border-background-200 hover:border-primary-300"
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    {isPending && (
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSelect(application.id)}
                        aria-label={`选择 ${application.product_name} 申请`}
                        className="mt-3 h-4 w-4 shrink-0 cursor-pointer accent-primary-600"
                      />
                    )}
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-file-list-3-line text-xl" />
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-foreground-950">
                        {application.product_name}
                      </h3>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                        <span className="rounded-md bg-background-100 px-2 py-0.5 text-foreground-700">
                          {editionLabel(application.edition)}
                        </span>
                        <span>{application.product_version || "—"}</span>
                        <span className="flex items-center gap-1">
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-computer-line" />
                          </span>
                          {application.seat_count} 席位
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-calendar-line" />
                          </span>
                          {formatFullDate(application.created_at)} 提交
                        </span>
                        {isRenewal && (
                          <span className="flex items-center gap-1 rounded-md bg-accent-100 px-2 py-0.5 font-medium text-accent-800">
                            <span className="flex h-3.5 w-3.5 items-center justify-center">
                              <i className="ri-refresh-line" />
                            </span>
                            续期申请
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <StatusBadge meta={meta} />
                </div>

                <div className="mt-4 grid gap-3 rounded-md border border-background-200 bg-background-100 px-4 py-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <p className="text-[11px] text-foreground-500">申请人</p>
                    <p className="mt-0.5 text-sm text-foreground-900">{application.applicant_name}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-foreground-500">联系邮箱</p>
                    <p className="mt-0.5 truncate text-sm text-foreground-900">{application.email}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-foreground-500">公司 / 组织</p>
                    <p className="mt-0.5 truncate text-sm text-foreground-900">{application.company || "—"}</p>
                  </div>
                  <div className="sm:col-span-2 lg:col-span-3">
                    <p className="text-[11px] text-foreground-500">机器码</p>
                    <p className="mt-0.5 truncate font-mono text-xs text-foreground-900">{application.machine_code || "—"}</p>
                  </div>
                  {application.use_case && (
                    <div className="sm:col-span-2 lg:col-span-3">
                      <p className="text-[11px] text-foreground-500">使用场景</p>
                      <p className="mt-0.5 text-sm leading-relaxed text-foreground-800">{application.use_case}</p>
                    </div>
                  )}
                </div>

                {application.reviewer_note && !isPending && (
                  <div className="mt-3 flex items-start gap-2 rounded-md bg-secondary-50 px-3 py-2.5 text-xs text-secondary-900">
                    <span className="mt-0.5 flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-chat-3-line" />
                    </span>
                    <span>审批备注：{application.reviewer_note}</span>
                  </div>
                )}

                {isPending && !openApprove && !openReject && (
                  <div className="mt-4 flex flex-wrap gap-2 border-t border-background-200 pt-4">
                    <button
                      type="button"
                      onClick={() => openPanel(application.id, "approve")}
                      className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-shield-check-line" />
                      </span>
                      通过并发证
                    </button>
                    <button
                      type="button"
                      onClick={() => openPanel(application.id, "reject")}
                      className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                    >
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-close-circle-line" />
                      </span>
                      驳回申请
                    </button>
                  </div>
                )}

                {openApprove && (
                  <div className="mt-4 rounded-md border border-primary-200 bg-primary-50 p-4">
                    <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                      <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                        <i className="ri-shield-check-line" />
                      </span>
                      {isRenewal ? "审批通过并顺延该许可证有效期" : "审批通过并自动签发许可证"}
                    </p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <label className="flex flex-col gap-1.5 text-xs text-foreground-600">
                        授权有效期
                        <select
                          value={months}
                          onChange={(event) => setMonths(Number(event.target.value))}
                          className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                        >
                          {DURATIONS.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="flex flex-col gap-1.5 text-xs text-foreground-600">
                        审批备注（选填）
                        <input
                          type="text"
                          value={note}
                          onChange={(event) => setNote(event.target.value)}
                          placeholder="例如：资料已核验通过"
                          className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                        />
                      </label>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          void submitApprove(application);
                        }}
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {busy ? "处理中…" : "确认通过并发证"}
                      </button>
                      <button
                        type="button"
                        onClick={closePanel}
                        className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                      >
                        取消
                      </button>
                    </div>
                  </div>
                )}

                {openReject && (
                  <div className="mt-4 rounded-md border border-accent-200 bg-accent-50 p-4">
                    <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                      <span className="flex h-4 w-4 items-center justify-center text-accent-700">
                        <i className="ri-close-circle-line" />
                      </span>
                      驳回该授权申请
                    </p>
                    <label className="mt-3 flex flex-col gap-1.5 text-xs text-foreground-600">
                      驳回原因（将展示给申请人）
                      <textarea
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        rows={3}
                        placeholder="例如：机器码与申请信息不匹配，请重新提交"
                        className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                      />
                    </label>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          void submitReject(application);
                        }}
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {busy ? "处理中…" : "确认驳回"}
                      </button>
                      <button
                        type="button"
                        onClick={closePanel}
                        className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                      >
                        取消
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        onChange={setPage}
        itemLabel="条申请"
      />

      <BatchConfirmModal
        open={confirmOpen}
        action={batch ?? "approve"}
        applications={selectedApplications}
        months={batchMonths}
        note={batchNote}
        busy={batchBusy}
        onConfirm={() => {
          void submitBatch();
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}